// Client-side sanitizer for in-app help article HTML, applied immediately
// before the `v-html` sink in `components/Help/HelpRenderer.vue`.
//
// WHY THIS EXISTS WHEN THE SERVER ALREADY SANITIZES
// -------------------------------------------------
// The backend renders help markdown and cleans it with nh3
// (`api/src/utils/help_markdown.py`), so this is defense in depth rather
// than the primary boundary. It is still worth having: the render sink is
// `v-html`, and the project's established posture (see
// `utils/sanitizeHtml.js`) is that every v-html sink sanitizes
// independently rather than trusting an upstream promise. If the help API
// were ever pointed at a different content source, or the server-side
// allowlist regressed, this keeps the sink safe on its own.
//
// Reuses DOMPurify -- the already-vetted client sanitizer (DEPENDENCIES.md
// row, 2026-07-20). No new dependency.
//
// The allowlist deliberately MIRRORS the server's
// (`_ALLOWED_TAGS` in `api/src/utils/help_markdown.py`): exactly the tags
// the help markdown renderer can emit. Notably absent: `img` (help is
// prose-first and screenshot-light per DOC-TENETS #2; images arrive later
// with a host restriction), `style`, `iframe`, and every form element.
// Keep the two lists in step -- if you widen one, widen the other. The
// same applies to ATTRIBUTES: the server allows `id` on `h1`-`h6` only, and
// the hook below holds this side to the identical rule.
//
// CONDITIONAL-TEXT MARKERS (plan Step B1, docs/planning/sa-settings-doc-plan.md)
// The server emits `<div data-help-when>`, `<div data-help-otherwise>` and
// `<span data-help-value>` around setting-dependent passages; a resolver
// picks a branch AFTER this sanitizer runs. The same per-tag, value-checked
// rule as the server applies here: block markers on `div` only, the value
// marker on `span` only, and only values in the renderer's grammar. The key
// allowlist itself lives server-side (`api/src/utils/help_context_keys.py`)
// and is not copied here: an unknown but well-formed key is inert on the
// client (the resolver finds no value and shows the authored fallback).
//
// COLLAPSIBLE SECTIONS (`::: details` in help markdown)
// The server emits `<details [open]><summary><hN id>..</hN></summary>..`.
// Same rule as the server: `details` may carry `open` and nothing else, with
// its value normalized to empty; `summary` carries no attribute at all.
// `open` is stripped from every other element. Help is public, so this
// widening is kept to exactly what the renderer emits.
import DOMPurify from "dompurify";

const ALLOWED_TAGS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "br",
  "hr",
  "ul",
  "ol",
  "li",
  "strong",
  "em",
  "code",
  "pre",
  "blockquote",
  "a",
  // Conditional-text markers only; see MARKER_ATTRS below.
  "div",
  "span",
  // Collapsible sections; see SECTION_TAGS below.
  "details",
  "summary",
];

// `href`/`title` on links, plus `rel`/`target` which the server stamps.
//
// `id` is here for HEADING ANCHORS: the server emits a slug id on every
// `h1`-`h6` so a reader can link at a section, and without `id` in this
// list DOMPurify strips those ids at the very last step -- the API response
// would be correct and the page would silently have no anchors.
const MARKER_WHEN_ATTR = "data-help-when";
const MARKER_OTHERWISE_ATTR = "data-help-otherwise";
const MARKER_VALUE_ATTR = "data-help-value";

const ALLOWED_ATTR = [
  "href",
  "title",
  "rel",
  "target",
  "id",
  MARKER_WHEN_ATTR,
  MARKER_OTHERWISE_ATTR,
  MARKER_VALUE_ATTR,
  "open",
];

// The collapsible-section tags. `details` keeps only the boolean `open`;
// `summary` keeps nothing (not even the globally allowed `title`).
const SECTION_TAGS = new Set(["details", "summary"]);

// Elements permitted to carry `id`. Mirrors the server's per-tag
// `_ALLOWED_ATTRIBUTES` entry in `api/src/utils/help_markdown.py`.
const ID_ALLOWED_TAGS = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);

// The marker grammar, mirroring `_CONDITION_RE` / `_KEY_PATTERN` in
// `api/src/utils/help_markdown.py`: a lowercase key, optionally preceded by
// `not ` and followed by `has <item>`; "not" and "has" are reserved. Values
// outside it -- quotes, angle brackets, colons, uppercase, extra spaces --
// are dropped, so the charset that can reach these attributes is
// `[a-z0-9_ ]`.
const KEY_PATTERN = "[a-z][a-z0-9_]*";
const CONDITION_RE = new RegExp(
  `^(?:not )?(${KEY_PATTERN})(?: has [a-z0-9_]+)?$`
);
const VALUE_KEY_RE = new RegExp(`^${KEY_PATTERN}$`);
const RESERVED_WORDS = new Set(["not", "has"]);

function isValidCondition(value) {
  const match = CONDITION_RE.exec(value);
  return match !== null && !RESERVED_WORDS.has(match[1]);
}

function isValidValueKey(value) {
  return VALUE_KEY_RE.test(value) && !RESERVED_WORDS.has(value);
}

// attribute -> [the one tag it may sit on, its value check]. A Map, not a
// plain object: the key is an attacker-controlled attribute name, and an
// object lookup would resolve `constructor` (or `__proto__`) to an
// Object.prototype member and crash the sanitizer.
const MARKER_ATTRS = new Map([
  [MARKER_WHEN_ATTR, ["div", isValidCondition]],
  [MARKER_OTHERWISE_ATTR, ["div", isValidCondition]],
  [MARKER_VALUE_ATTR, ["span", isValidValueKey]],
]);

// DOMPurify's `ALLOWED_ATTR` is GLOBAL -- it has no per-tag form and no
// value check, so the entries above would otherwise permit `id` and the
// marker attributes on every allowed element, with any value. This hook
// narrows them back to exactly the server's rule:
//
//   * `id` on `h1`-`h6` only;
//   * `data-help-when` / `data-help-otherwise` on `div` only, and
//     `data-help-value` on `span` only, each with a grammar-valid value;
//   * `open` (value emptied) on `details` only, and nothing else at all on
//     `details` or `summary`.
//
// It inspects only the tag name and attribute value and can only REMOVE an
// attribute (or empty the value of `open`), so it cannot make anything more
// permissive than the allowlist already decided.
//
// Why bother for `id`, a fairly inert attribute? Because an article heading
// that could mint an id on an arbitrary element could shadow an app element
// id and break a `getElementById` elsewhere on the page, and because a
// sanitizer whose two halves disagree is a sanitizer nobody can reason about
// later. Keeping them identical is the point.
//
// Registered per call and removed in `finally` (the `sanitizeHtml.js`
// pattern): DOMPurify hooks are global to the shared default instance, and
// a module-scope hook would apply this policy to every other caller.
function helpAttributeHook(node, data) {
  const tagName = (node.tagName || "").toLowerCase();

  if (SECTION_TAGS.has(tagName)) {
    if (tagName === "details" && data.attrName === "open") {
      // Boolean: presence is the meaning, so no authored value survives.
      data.attrValue = "";
    } else {
      data.keepAttr = false;
    }
    return;
  }

  if (data.attrName === "open") {
    data.keepAttr = false;
    return;
  }

  if (data.attrName === "id") {
    if (!ID_ALLOWED_TAGS.has(tagName)) {
      data.keepAttr = false;
    }
    return;
  }

  const marker = MARKER_ATTRS.get(data.attrName);
  if (marker) {
    const [allowedTag, isValid] = marker;
    if (tagName !== allowedTag || !isValid(data.attrValue)) {
      data.keepAttr = false;
    }
  }
}

/**
 * Sanitize help-article HTML for rendering via v-html.
 *
 * @param {string} dirty Raw HTML from the help API.
 * @returns {string} Sanitized HTML safe for a v-html sink.
 */
export function sanitizeHelpHtml(dirty) {
  if (!dirty || typeof dirty !== "string") {
    return "";
  }

  DOMPurify.addHook("uponSanitizeAttribute", helpAttributeHook);
  try {
    return DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      // Drop the element AND its content for anything outside the
      // allowlist, so a stripped <script> cannot leave its source text
      // visible as prose.
      FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
      FORBID_ATTR: ["srcset", "formaction", "xlink:href"],
      // No `data:`/`javascript:` URLs; DOMPurify's default URI policy
      // already blocks them, and the tight ALLOWED_TAGS list leaves no src
      // sink.
      //
      // Generic `data-*` stays OFF: the three marker attributes are named
      // in ALLOWED_ATTR explicitly, so no other data attribute survives.
      ALLOW_DATA_ATTR: false,
    });
  } finally {
    // Remove exactly the hook added above (by reference), never a hook some
    // other caller registered.
    DOMPurify.removeHook("uponSanitizeAttribute", helpAttributeHook);
  }
}

export default sanitizeHelpHtml;
